/**
 * SEO Component
 * Handles meta tags, Open Graph, Twitter Cards, and structured data
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SEO = ({
  title,
  description,
  image,
  type = 'website',
  author = 'Richard Plotka',
  keywords = [],
  publishedTime,
  modifiedTime,
  structuredData,
}) => {
  const location = useLocation();
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://www.richardplotka.com';
  const fullUrl = `${siteUrl}${location.pathname}`;
  const defaultImage = `${siteUrl}/Rlogo.png`;
  const imageUrl = image || defaultImage;
  const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : `${siteUrl}${imageUrl}`;

  // Default title and description
  const defaultTitle = 'Richard Plotka - Professor, Innovator & Technology Leader';
  const defaultDescription = 'Professional portfolio of Richard Plotka - Professor, Innovator & Technology Leader. Explore projects, research, and teaching materials.';
  
  const pageTitle = title ? `${title} | Richard Plotka` : defaultTitle;
  const pageDescription = description || defaultDescription;
  const keywordsString = keywords.length > 0 ? keywords.join(', ') : 'Richard Plotka, Portfolio, Professor, Technology, Innovation, Web Development, Teaching';

  // Generate structured data
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Richard Plotka',
    url: siteUrl,
    jobTitle: 'Professor',
    description: pageDescription,
    image: fullImageUrl,
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={keywordsString} />
      <meta name="author" content={author} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content="Richard Plotka" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={fullImageUrl} />

      {/* Article specific (for blog posts) */}
      {type === 'article' && (
        <>
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          <meta property="article:author" content={author} />
        </>
      )}

      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  type: PropTypes.oneOf(['website', 'article', 'profile']),
  author: PropTypes.string,
  keywords: PropTypes.arrayOf(PropTypes.string),
  publishedTime: PropTypes.string,
  modifiedTime: PropTypes.string,
  structuredData: PropTypes.object
};

SEO.defaultProps = {
  title: null,
  description: null,
  image: null,
  type: 'website',
  author: 'Richard Plotka',
  keywords: [],
  publishedTime: null,
  modifiedTime: null,
  structuredData: null
};

// Memoize SEO component to prevent unnecessary re-renders
export default React.memo(SEO);

